import React, { useMemo, useState } from "react";
import { useTable, useGlobalFilter } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const rawData = [
    {
        id: "fff6b6fa",
        letter_num: "HHPL/HSP-PKG-3/HAM/22-23/231",
        project_pr_code: "IE/009",
        project_short_name: "HARDOI BYPASS",
        letterExchange: "receive",
        letter_date: "2023-01-13",
        subject: "Request for deposition of supervision charges for electrical utility",
        letter_from: "Concessionaire",
        letter_to: "Authority",
        status: "pending",
        lettertype: "informative",
        contractorname: "M/S PNC Infratech Ltd.",
    },
    {
        id: "ffb1100d",
        letter_num: "GAWAR/PADHAR-BIJNI/NH-154/PKG-VA/2025/436",
        project_pr_code: "IE/011",
        project_short_name: "PADHAR BIJNI",
        letterExchange: "receive",
        letter_date: "2025-03-22",
        subject: "Regarding danger and loss to forest rest house...",
        letter_from: "Contractor",
        letter_to: "PD NHAI, Mandi",
        status: "pending",
        lettertype: "other",
        contractorname: "M/s Gawar Pathankot Highway Pvt. Ltd.",
    },
    {
        id: "ff90b625",
        letter_num: "SAPL/RKCTC/2024-25/TL/149",
        project_pr_code: "AE/050",
        project_short_name: "RAFIABAD CHAMKOT",
        letterExchange: "send",
        letter_date: "2025-04-05",
        subject: "Observation on Revised Submission of Superstructure...",
        letter_from: "SO",
        letter_to: "M/S Choudhary Power Projects Pvt. Ltd",
        status: "pending",
        lettertype: "Design & Drawing",
        contractorname: "Gammon Engineers and Contractors Pvt Ltd",
    },
];

const LetterTable = () => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [visibleCols, setVisibleCols] = useState([
        "letter_num",
        "project_pr_code",
        "project_short_name",
        "letterExchange",
        "letter_date",
        "subject",
        "letter_from",
        "letter_to",
        "status",
        "lettertype",
        "contractorname",
    ]);

    const columns = useMemo(() => {
        const allCols = [
            { Header: "Letter No", accessor: "letter_num" },
            { Header: "Project Code", accessor: "project_pr_code" },
            { Header: "Project Name", accessor: "project_short_name" },
            { Header: "Exchange", accessor: "letterExchange" },
            { Header: "Date", accessor: "letter_date" },
            { Header: "Subject", accessor: "subject" },
            { Header: "From", accessor: "letter_from" },
            { Header: "To", accessor: "letter_to" },
            { Header: "Status", accessor: "status" },
            { Header: "Type", accessor: "lettertype" },
            { Header: "Contractor", accessor: "contractorname" },
        ];
        return allCols.filter((col) => visibleCols.includes(col.accessor));
    }, [visibleCols]);

    const data = useMemo(() => {
        return rawData.filter((row) =>
            Object.values(row).join(" ").toLowerCase().includes(globalFilter.toLowerCase())
        );
    }, [globalFilter]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        { columns, data },
        useGlobalFilter
    );

    const exportToXLSX = () => {
        const exportData = rows.map((row) => row.values);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Letters");
        const xlsBlob = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([xlsBlob]), "letters.xlsx");
    };

    const allColumnAccessors = [
        "letter_num",
        "project_pr_code",
        "project_short_name",
        "letterExchange",
        "letter_date",
        "subject",
        "letter_from",
        "letter_to",
        "status",
        "lettertype",
        "contractorname",
    ];

    return (
        <div className="p-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <input
                    type="text"
                    placeholder="Search..."
                    className="border p-2 rounded w-full sm:w-1/3"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                />
                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    onClick={exportToXLSX}
                >
                    Download XLSX
                </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-3">
                {allColumnAccessors.map((key) => (
                    <label key={key} className="text-sm flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={visibleCols.includes(key)}
                            onChange={() =>
                                setVisibleCols((prev) =>
                                    prev.includes(key)
                                        ? prev.filter((col) => col !== key)
                                        : [...prev, key]
                                )
                            }
                        />
                        {key}
                    </label>
                ))}
            </div>

            <div className="overflow-auto border rounded">
                <table {...getTableProps()} className="min-w-full text-sm text-left">
                    <thead className="bg-gray-100">
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps()}
                                        className="border p-2 font-semibold"
                                    >
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.length > 0 ? (
                            rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="hover:bg-gray-50">
                                        {row.cells.map((cell) => (
                                            <td {...cell.getCellProps()} className="border p-2">
                                                {cell.render("Cell")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="text-center p-4 text-gray-500"
                                >
                                    No matching records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LetterTable;
